import React from 'react';
import { Instagram, ArrowUpRight, Heart, MessageCircle } from 'lucide-react';
import { REFERENCES } from '../mock/data';
import { Reveal } from '../hooks/useReveal';
import MagneticButton from './MagneticButton';

const INSTAGRAM_URL = 'https://www.instagram.com/frachigershandwerk/';
const FEATURED_POST_URL = 'https://www.instagram.com/p/DZxj9X_CAPh/';

// NOTE: Without official Instagram API access we cannot reliably fetch live post thumbnails.
// The featured tile links to the specific highlighted post; supporting tiles use our own
// verified project photos as visual preview and link to the profile. No fake posts, no
// invented content — everything either links to the real profile or the real post.
const supporting = [
  REFERENCES[1],
  REFERENCES[10],
  REFERENCES[14],
  REFERENCES[7],
  REFERENCES[15],
];

const InstagramSection = () => {
  return (
    <section
      className="py-24 md:py-28 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden"
      data-testid="instagram-section"
      aria-labelledby="instagram-heading"
    >
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand-blueMist/50 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">
                <Instagram className="w-4 h-4" />
                @frachigershandwerk
              </div>
              <h2
                id="instagram-heading"
                className="font-display text-4xl md:text-5xl text-brand-blueDark leading-tight tracking-tight"
              >
                Folge uns auf <span className="italic text-brand-blue">Instagram</span>
              </h2>
              <p className="text-brand-ink/70 mt-4 leading-relaxed">
                Aktuelle Projekte, Einblicke hinter die Kulissen und Referenzen aus unserer täglichen Handwerksarbeit.
              </p>
            </div>
            <MagneticButton>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 bg-brand-blueDark hover:bg-brand-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors shadow-xl shadow-brand-blueDark/25 whitespace-nowrap"
                data-testid="instagram-visit-btn"
              >
                <Instagram className="w-4 h-4" />
                Instagram besuchen
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </MagneticButton>
          </div>
        </Reveal>

        {/* Featured post + supporting tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {/* Featured — spans 2 cols x 2 rows */}
          <Reveal className="col-span-2 md:col-span-2 md:row-span-2">
            <a
              href={FEATURED_POST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block w-full aspect-square md:aspect-auto md:h-full overflow-hidden rounded-md bg-brand-blueDark lift-on-hover"
              data-testid="instagram-featured-post"
              aria-label="Neuester Instagram-Beitrag anzeigen"
            >
              <img
                src={REFERENCES[4].image}
                alt="Neuester Instagram-Beitrag von @frachigershandwerk"
                loading="lazy"
                className="w-full h-full object-cover opacity-90 group-hover:scale-110 group-hover:opacity-100 transition-all duration-1000 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark/95 via-brand-blueDark/20 to-transparent"></div>

              {/* Instagram gradient accent border */}
              <div className="absolute inset-0 rounded-md pointer-events-none border-2 border-transparent group-hover:border-white/40 transition-colors"></div>

              {/* IG badge */}
              <div className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white text-brand-blueDark flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Instagram className="w-5 h-5" />
              </div>
              <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-brand-blueDark text-[10px] uppercase tracking-widest font-bold">
                Neuester Post
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-4 text-white/85 text-sm mb-3">
                  <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4" /> Post ansehen</span>
                  <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> Kommentar</span>
                </div>
                <div className="font-display text-2xl md:text-3xl text-white leading-tight mb-1">
                  @frachigershandwerk
                </div>
                <div className="text-brand-blueSky text-xs uppercase tracking-widest font-bold">
                  Auf Instagram öffnen →
                </div>
              </div>
            </a>
          </Reveal>

          {/* Supporting tiles */}
          {supporting.map((r, i) => (
            <Reveal key={r.id} delay={i * 60}>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block w-full aspect-square overflow-hidden rounded-md bg-brand-beige lift-on-hover"
                data-testid={`instagram-tile-${i}`}
                aria-label="Weitere Beiträge auf @frachigershandwerk ansehen"
              >
                <img
                  src={r.image}
                  alt="Projekt-Impression"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-brand-blueDark/0 group-hover:bg-brand-blueDark/60 transition-colors flex items-center justify-center">
                  <Instagram className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all" />
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-8 text-center">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-brand-blueDark hover:text-brand-blue text-sm font-bold uppercase tracking-wider transition-colors"
              data-testid="instagram-more-link"
            >
              Mehr auf Instagram ansehen
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default InstagramSection;
